import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
import {Router} from '@angular/router';
import {UserService} from './shared/user.service';

import {
    MsalService,
    MsalBroadcastService
} from '@azure/msal-angular';
import {
    EventMessage,
    EventType,
    InteractionStatus,
} from '@azure/msal-browser';
import {User} from './model/user';
import {StatisticsService} from './shared/statistics.service';
import {StatisticsAssignmentNoConsultant} from './model/statisticsassignmentnoconsultant';
import {StatisticsAssignmentConsultant} from './model/statisticsassignmentconsultant';

import {ReturnObject} from './model/returnobject';
import * as FileSaver from 'file-saver';
import {AccessRoleItem} from './model/accessroleitem';
import {InternalMsgService} from './shared/internal.msg.service';
import {RoleGdprItem} from './model/rolegdpritem';
import {ConsultantGdprItem} from './model/consultantgdpritem';
import {SysadminServiceService} from './shared/sysadmin-service.service';
import {StatusMessagesService} from './shared/statusmessage.service';
import { CommonService } from './shared/common.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    appName = 'CAFS';
    accessRoles: AccessRoleItem[] = [];
    loginDisplay = false;
    private readonly _destroying$ = new Subject<void>();
    // ******************************************************************
    gdprForConsultant: string = '-1';
    gdprForUser: string = '';
    emulateEmail: string = '';
    showModal: boolean = false;
    dlgType: string = '';

    constructor(
        // ******************************************************************
        private authService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        // ******************************************************************
        private router: Router,
        private userService: UserService,
        private commonService: CommonService,
        private sysadminService: SysadminServiceService,
        private statisticsService: StatisticsService,
        private msgService: InternalMsgService,
        private statusMessagesService: StatusMessagesService) {
    }

    ngOnInit() {
        this.authService.handleRedirectObservable().subscribe();

        this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
        this.msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) =>
                    msg.eventType === EventType.ACCOUNT_ADDED ||
                    msg.eventType === EventType.ACCOUNT_REMOVED
                ),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                if (this.authService.instance.getAllAccounts().length === 0) {
                    window.location.pathname = '/';
                } else {
                    this.setLoginDisplay();
                }
            });
        this.msalBroadcastService.inProgress$
            .pipe(
                // Filtering for all interactions to be completed
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe((status: InteractionStatus) => {
                    // if (this.checkAccount() && status === InteractionStatus.None) {
                    this.setLoginDisplay();
                    this.checkAndSetActiveAccount();
                    if (this.loginDisplay) {
                        this.gotoHome();
                    }
                }
            );
    }

    checkAndSetActiveAccount(): void {
        /**
         * If no active account set but there are accounts signed in, sets first account to active account
         * To use active account set here, subscribe to inProgress$ first in your component
         * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
         */
        let activeAccount = this.authService.instance.getActiveAccount();
        if (
            !activeAccount &&
            this.authService.instance.getAllAccounts().length > 0
        ) {
            let accounts = this.authService.instance.getAllAccounts();
            this.userService.setAzureUserName(accounts[0].username);
            this.authService.instance.setActiveAccount(accounts[0]);

        }
    }

    ngOnDestroy() {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    actAsUser(): void {
        this.sysadminService.actAsUser(`${this.emulateEmail}`)
            .subscribe({
                    next: (data: ReturnObject<User>) => {
                        if (data.isFailure) {
                            this.statusMessagesService.showToasterError('Error', data.failureMessage);
                            return;
                        }

                        this.userService.setUser(data.value);
                        window.location.reload();
                    },
                    error: (err) => {
                        this.statusMessagesService.showHttpError(err);
                    },
                    complete: () => {
                        //
                    }
                }
            );
    }

    stopActAsUser(): void {
        this.sysadminService.stopActAsUser()
            .subscribe({
                    next: (data: ReturnObject<User>) => {
                        if (data.isFailure) {
                            this.statusMessagesService.showToasterError('Error', data.failureMessage);
                            return;
                        }

                        this.userService.setUser(data.value);
                        window.location.reload();
                    },
                    error: (err) => {
                        this.statusMessagesService.showHttpError(err);
                    },
                    complete: () => {
                        //
                    }
                }
            );
    }

    GetRoleGdpr(gdprForUser: string): void {
        this.statisticsService.GetRoleGdpr(gdprForUser)
            .subscribe({
                    next: (data: ReturnObject<RoleGdprItem>) => {
                        if (data.isFailure) {
                            this.statusMessagesService.showToasterError('Error', data.failureMessage);
                            return;
                        }

                        if (data.value.role.globalId.startsWith('NO GDPR INFOR FOUND')) {
                            this.statusMessagesService.showToasterInfo('Nothing found', data.failureMessage);
                            return;
                        }

                        const gdpr: RoleGdprItem = data.value;
                        const role = gdpr.role;
                        let text = 'EMPLOYEE\n'
                            + 'Name'.padEnd(20, ' ') + ': ' + role.empName.toUpperCase() + '\n'
                            + 'Global id'.padEnd(20, ' ') + ': ' + role.globalId + '\n'
                            + 'Email'.padEnd(20, ' ') + ': ' + role.email + '\n'
                            + 'Department'.padEnd(20, ' ') + ': ' + role.department + '\n'
                            + 'Employee role'.padEnd(20, ' ') + ': ' + role.empRole + '\n'
                            + 'Added manually'.padEnd(20, ' ') + ': ' + role.addedmanually + '\n'
                            + 'Added date'.padEnd(20, ' ') + ': ' + (role.addedtime ? role.addedtime.toString().substring(0, 10) : '') + '\n\n';

                        if (gdpr.relatedData != null) {

                            gdpr.relatedData.forEach(detailedData => {
                                const ass = detailedData.assignment;
                                text = text + '    ASSIGNMENT\n'
                                    + '    Number'.padEnd(22, ' ') + ': ' + ass.assignmentNumber + '\n'
                                    + '    Start date'.padEnd(22, ' ') + ': ' + (ass.assignmentStartdate ? ass.assignmentStartdate.toString().substring(0, 10) : '') + '\n'
                                    + '    End date'.padEnd(22, ' ') + ': ' + (ass.assignmentEnddate ? ass.assignmentEnddate.toString().substring(0, 10) : '') + '\n'
                                    + '    Dispensation'.padEnd(22, ' ') + ': ' + ass.dispensation + '\n'
                                    + '    Extension'.padEnd(22, ' ') + ': ' + (ass.extentionAfter12Mnths ? ass.extentionAfter12Mnths.toString().substring(0, 10) : '') + '\n'
                                    + '    Added date'.padEnd(22, ' ') + ': ' + (ass.addedtime ? ass.addedtime.toString().substring(0, 10) : '') + '\n\n';

                                detailedData.consultants.forEach(consultant => {
                                    const cons = consultant;
                                    text = consultant
                                        ? text + '        CONSULTANT\n'
                                        + '        Name'.padEnd(31, ' ') + ': ' + cons.consultantName + '\n'
                                        + '        Consultant number'.padEnd(31, ' ') + ': ' + cons.consultantNumber + '\n'
                                        + '        Start date'.padEnd(31, ' ') + ': ' + (cons.consultantStartdate ? cons.consultantStartdate.toString().substring(0, 10) : '') + '\n'
                                        + '        End date'.padEnd(31, ' ') + ': ' + (cons.consultantEnddate ? cons.consultantEnddate.toString().substring(0, 10) : '') + '\n'
                                        + '        Assignment reason'.padEnd(31, ' ') + ': ' + cons.reasonForEmplNeed + '\n'
                                        + '        Unit number'.padEnd(31, ' ') + ': ' + cons.unitNumber + '\n'
                                        + '        Added date'.padEnd(31, ' ') + ': ' + (cons.addedtime ? cons.addedtime?.toString().substring(0, 10) : '') + '\n\n'
                                        : text + '        CONSULTANT\n'
                                        + '        Name'.padEnd(31, ' ') + ': NOT FOUND\n';
                                });
                                text = text + '    --------------------------------------------------------------------------------\n\n'

                            });
                        }

                        const stamp = new Date().toISOString().replace(/[:]/g, '-');
                        const fileName = `role-gdpr-info-${gdpr.role.globalId}-${stamp}.txt`;
                        const file = new Blob([text], { type: 'text/plain;charset=utf-8' });
                        FileSaver.saveAs(file, fileName, { autoBom: true });

                    },
                    error: (err) => {
                        this.statusMessagesService.showHttpError(err);
                    },
                    complete: () => {
                        //
                    }
                }
            );
    }

    GetConsultantGdpr(gdprForConsultant: string): void {
        this.statisticsService.GetConsultantGdpr(gdprForConsultant)
            .subscribe({
                    next: (data: ReturnObject<ConsultantGdprItem>) => {
                        if (data.isFailure) {
                            this.statusMessagesService.showToasterError('Error', data.failureMessage);
                            return;
                        }

                        if (data.value.consultant.consultantName.startsWith('NO GDPR INFO FOUND')) {
                            this.statusMessagesService.showToasterInfo('Nothing found', data.failureMessage);
                            return;
                        }

                        const gdpr: ConsultantGdprItem = data.value;
                        const consultant = gdpr.consultant;
                        let text = 'CONSULTANT\n'
                            + 'Name'.padEnd(23, ' ') + ': ' + consultant.consultantName.toUpperCase() + '\n'
                            + 'Number'.padEnd(23, ' ') + ': ' + consultant.consultantNumber + '\n'
                            + 'End date'.padEnd(23, ' ') + ': ' + (consultant.consultantEnddate ? consultant.consultantEnddate.toString().substring(0, 10) : '') + '\n'
                            + 'Start date'.padEnd(23, ' ') + ': ' + (consultant.consultantStartdate ? consultant.consultantStartdate.toString().substring(0, 10) : '') + '\n'
                            + 'Assignment reason'.padEnd(23, ' ') + ': ' + consultant.reasonForEmplNeed + '\n'
                            + 'Unit number'.padEnd(23, ' ') + ': ' + consultant.unitNumber + '\n'
                            + 'Added date'.padEnd(23, ' ') + ': ' + (consultant.addedtime ? consultant.addedtime.toString().substring(0, 10) : '') + '\n\n';

                        if (gdpr.relatedData != null) {

                            gdpr.relatedData.forEach(detailedData => {
                                const ass = detailedData.assignment;
                                text = text + '    ASSIGNMENT\n'
                                    + '    Number'.padEnd(24, ' ') + ': ' + ass.assignmentNumber + '\n'
                                    + '    Start date'.padEnd(24, ' ') + ': ' + (ass.assignmentStartdate ? ass.assignmentStartdate.toString().substring(0, 10) : '') + '\n'
                                    + '    End date'.padEnd(24, ' ') + ': ' + (ass.assignmentEnddate ? ass.assignmentEnddate.toString().substring(0, 10) : '') + '\n'
                                    + '    Dispensation'.padEnd(24, ' ') + ': ' + ass.dispensation + '\n'
                                    + '    Extension '.padEnd(24, ' ') + ': ' + (ass.extentionAfter12Mnths ? ass.extentionAfter12Mnths.toString().substring(0, 10) : '') + '\n'
                                    + '    Added date'.padEnd(24, ' ') + ': ' + (ass.addedtime ? ass.addedtime.toString().substring(0, 10) : '') + '\n\n';

                                const role = detailedData.role;
                                text = role
                                    ? text + '    MANAGER\n'
                                    + '    Name'.padEnd(24, ' ') + ': ' + role.empName.toUpperCase() + '\n'
                                    + '    Global id'.padEnd(24, ' ') + ': ' + role.globalId + '\n'
                                    + '    Email'.padEnd(24, ' ') + ': ' + role.email + '\n'
                                    + '    Department'.padEnd(24, ' ') + ': ' + role.department + '\n'
                                    + '    Employee role'.padEnd(24, ' ') + ': ' + role.empRole + '\n'
                                    + '    Added manually'.padEnd(24, ' ') + ': ' + role.addedmanually + '\n'
                                    + '    Added date'.padEnd(24, ' ') + ': ' + (role.addedtime ? role.addedtime.toString().substring(0, 10) : '') + '\n'
                                    + '    --------------------------------------------------------------------------------\n\n'
                                    : text + '    MANAGER\n'
                                    + '    Name'.padEnd(24, ' ') + ': NOT FOUND\n';
                            });

                            text = text
                        }

                        var dt = new Date();
                        var fileName = `consultant-gdpr-info-${gdpr.consultant.consultantNumber}-${dt.toString().substring(0, 24)}.txt`;
                        var file = new Blob([text], {type: "text/plain;charset=utf-8"});
                        FileSaver.saveAs(file, fileName, {autoBom: true});
                    },
                    error: (err) => {
                        this.statusMessagesService.showHttpError(err);
                    },
                    complete: () => {
                        //
                    }
                }
            );
    }

    GetAssignmentConsultants(): void {
        this.statisticsService.GetAssignmentConsultants()
            .subscribe({
                    next: (data: ReturnObject<StatisticsAssignmentConsultant[]>) => {
                        if (data.isFailure) {
                            this.statusMessagesService.showToasterError('Error', data.failureMessage);
                            return;
                        }

                        let csv = 'ASSIGNMENT-NUMBER;ASSIGNMENT-STARTDATE;ASSIGNMENT-ENDDATE;EXTENSION-AFTER-12MONTH;DEPARTMENT;CONSULTANT-NAME;CONSULTANT-NUMBER;UNIT-NUMBER;CONSULTANT-STARTDATE;CONSULTANT-ENDDATE;HAS-DISPENSATION;REASON\n';
                        data.value.forEach(x => {
                            csv = csv
                                + x.assignmentNumber + ';'
                                + x.assignmentStartdate + ';'
                                + x.assignmentEndDate + ';'
                                + x.extensionAfter12Months + ';'
                                + x.department + ';'
                                + x.consultantName + ';'
                                + x.consultantNumber + ';'
                                + x.unitNumber + ';'
                                + x.consultantStartDate + ';'
                                + x.consultantEndDate + ';'
                                + x.hasDispensation + ';'
                                + x.reason + '\n';
                        })
                        var dt = new Date();
                        var fileName = 'consultant-assignments-' + dt.toLocaleDateString() + '.csv';
                        var file = new Blob([csv], {type: "text/plain;charset=utf-8"});
                        FileSaver.saveAs(file, fileName, {autoBom: true});
                    },
                    error: (err) => {
                        this.statusMessagesService.showHttpError(err);
                    },
                    complete: () => {
                        //
                    }
                }
            );
    }

    GetAssignmentNoConsultants(): void {
        this.statisticsService.GetAssignmentNoConsultants()
            .subscribe({
                    next: (data: ReturnObject<StatisticsAssignmentNoConsultant[]>) => {
                        if (data.isFailure) {
                            this.statusMessagesService.showToasterError('Error', data.failureMessage);
                            return;
                        }

                        let csv = 'ASSIGNMENT-NUMBER;DEPARTMENT;ASSIGNMENT-STARTDATE;ASSIGNMENT-ENDDATE;EXTENSION-AFTER-12MONTH;HAS-DISPENSATION;STATUS\n';
                        data.value.forEach(x => {
                            csv = csv
                                + x.assignmentNumber + ';'
                                + x.department + ';'
                                + x.startdate + ';'
                                + x.endDate + ';'
                                + x.extensionAfter12Months + ';'
                                + x.hasDispensation + ';'
                                + x.status + '\n';
                        })
                        var dt = new Date();
                        var fileName = 'assignments-noconsultants' + dt.toLocaleDateString() + '.csv';
                        var file = new Blob([csv], {type: "text/plain;charset=utf-8"});
                        FileSaver.saveAs(file, fileName, {autoBom: true});
                    },
                    error: (err) => {
                        this.statusMessagesService.showHttpError(err);
                    },
                    complete: () => {
                        //
                    }
                }
            );
    }


    getGdprForThisUder(): void {
        this.gdprForUser = this.loggedUser().id;
        this.GetRoleGdpr(this.loggedUser().id);
    }

    getGdprForUser(): void {
        if (this.loggedUser().accessFeatures.find(x => x.includes('statistics'))) {
            this.openUserGdprDialog()
        } else {
            this.gdprForUser = this.loggedUser().id;
            this.GetRoleGdpr(this.gdprForUser);
        }
    }

    getRoleGdprMenuText(): string {
        if (this.loggedUser().accessFeatures.find(x => x.includes('statistics'))) {
            return 'User GDPR info';
        }
        return this.getYourStatMenuText();
    }

    getYourStatMenuText(): string {
        return `Your (${this.userService.getUser().id}) GDPR info`;
    }

    loggedUser(): User {
        return this.userService.getUser();
    }

    reverseName(name: string): string {
        return this.commonService.reverseName(name);
    }

    setLoginDisplay(): void {
        this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    }

    logout(): void {
        this.userService.setUser(User.getEmpty());
        this.authService.logout();
    }

    gotoHome(): void {
        this.router.navigate([`/home`]);
    }

    isSysAdmin(): boolean {
        return this.userService.isAdminUSer();
    }

    isStatUser(): boolean {
        return this.userService.isStatUser();
    }

    isActingAsOtherUser(): boolean {
        return this.userService.isActingAsOtherUser();
    }

    openCheckBatchStatus(): void {
        this.dlgType = 'batchjobstatusdlg';
    }

    openSysadminDialog(): void {
        this.dlgType = 'sysadmindlg';
    }

    openConsultanGdprDialog(): void {
        this.dlgType = 'consultantgdprdlg';
    }

    openUserGdprDialog(): void {
        this.dlgType = 'usergdprdlg';
    }

    onCloseDlg($event: any) {
        if (this.dlgType == 'usergdprdlg') {
            if ($event !== '') {
                this.GetRoleGdpr($event);
            }
        }

        if (this.dlgType == 'consultantgdprdlg') {
            if ($event !== '') {
                this.GetConsultantGdpr($event);
            }
        }

        if (this.dlgType == 'sysadmindlg') {
            if ($event !== '') {
                // Close sysadmindlg dont have an action - here only for debug purpose
            }
        }

        if (this.dlgType == 'batchjobstatusdlg') {
            if ($event !== '') {
                // Close batchjobstatusdlg dont have an action - here only for debug purpose
            }
        }

        this.dlgType = ''
    }

}
